import {
  assign,
  chain,
  first,
  get,
  includes,
  isEmpty,
  keyBy,
  keys,
  map,
  noop,
  omit,
  range,
  reduce,
  size,
  startCase,
  sumBy,
  toUpper,
  values,
} from 'lodash';
import moment from 'moment';
import { DateTime } from 'luxon';
import * as XLSX from 'xlsx';
import UserAPI from '../../Api/user';
import BookingAPI from '../../Api/booking';
import VendorAPI from '../../Api/vendor.ts';
import FactoryAPI from '../../Api/factory.ts';
import PurchaseOrderAPI from '../../Api/purchase-order';
import makeFactoryStatus from '../Factory/make-factory-status';
import makeTopStatuses from '../Role/make-top-statuses';
import InfluencerAPI from '../../Api/influencer.ts';
import CatalogItemAPI from '../../Api/catalog-item';

const deliveriesColumns = [
  'reference',
  'purchase_orders',
  'starts_at',
  'ends_at',
  'boxes',
  'boxes_received',
  'units',
  'delivered_at',
];

const paymentColumns = [
  'payment_date',
  'invoice_number',
  'check_number',
  'total',
];
const orderColumns = [
  'po_number',
  'due_date',
  'status',
  'is_accepted',
  'date_of_acceptance',
  'topStatus',
];

const billColumns = ['invoice_number', 'status', 'po_number', 'total'];

const creditColumns = ['date', 'reference_number', 'memo', 'amount'];

const vendorGradeColumns = ['company_name', 'grade', 'grade_updated_at'];

const complianceReportColumns = [
  'company_name',
  'email',
  'last_logged_in_at',
  'coi_expiration_date',
];

const factoryColumns = [
  'vendor.company_name',
  'legal_name',
  'contact_name',
  'contact_email',
  'contact_phone',
  'audit_expiration_date',
  'garment_registration_number',
  'status',
  'address',
  'address_2',
  'city',
  'state',
  'country',
  'zip',
];

const influencersColumns = [
  'registered_at',
  'first_name',
  'last_name',
  'email',
  'last_logged_in_at',
  { label: 'company name', key: 'vendor.company_name' },
  { label: 'Country', key: 'vendor.country' },
  { label: 'Phone', key: 'vendor.phone' },
  { label: 'Primary Handle', key: 'vendor.influencer_primary_handle' },
  { label: 'Handle', key: 'vendor.influencer_handle' },
  { label: 'Platform', key: 'vendor.influencer_platform' },
  { label: 'Application Status', key: 'vendor.application_status' },
  { label: 'Agreement Uploaded', key: 'vendor.vendor_agreement.exists' },
  { label: 'Approved At', key: 'vendor.approved_at' },
];

const catalogItemColumns = [
  { label: 'Style Number', key: 'vendor_style_number' },
  'size',
  'width',
  'length',
  'height',
  'weight',
  { label: 'Last Received', key: 'last_po_receipt_date' },
  { label: 'FN Item Number', key: 'sku' },
  { label: 'WMS Number', key: 'wms_id' },
];

const pickDeep = (object, arrayOfPaths) =>
  reduce(
    arrayOfPaths,
    (r, path) => {
      const label = path instanceof Object ? path.label : path;
      const key = path instanceof Object ? path.key : path;
      return { ...r, [label]: get(object, key) };
    },
    {},
  );

const formatVal = (v) => {
  if (v === undefined) return '';
  if (v === null) return '';
  if (typeof v === 'string') return v.replace(/,/g, ' ');

  return v;
};

const downloadAsCSV = (collection, columns, name) => {
  const picked = map(collection, (row) => pickDeep(row, columns));
  for (let i = 0; i < picked.length; i += 1) {
    if (picked[i].purchase_orders) {
      picked[i].purchase_orders = picked[i].purchase_orders.reduce(
        (accumulator, currentValue, currentIndex, array) => {
          const isLastPoInArray = currentIndex + 1 === array.length;
          const poNumber = currentValue.po_number
            ? currentValue.po_number.trim()
            : '';
          return `${accumulator + poNumber}${isLastPoInArray ? '' : ' - '}`;
        },
        '',
      );
    }
  }
  const headers = [keys(first(picked)).map(startCase).join(', ')];
  const csv = headers
    .concat(map(picked, (row) => values(row).map(formatVal).join(', ')))
    .join('\n');
  const data = `data:text/csv; charset=utf-8, ${encodeURIComponent(csv)}`;
  const link = document.createElement('a');
  const date = moment().format('YYYYMMDDHHmm');
  link.setAttribute('href', data);
  link.setAttribute('download', `${name}_${date}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export async function deliveriesExport(userId, options) {
  const deliveries = userId
    ? await UserAPI.getVendorDeliveriesExport(userId, options)
    : await BookingAPI.getBookingsExport(options);
  downloadAsCSV(deliveries, deliveriesColumns, 'deliveries');
  return deliveries;
}

const formatOrders = (orders) =>
  chain(orders)
    .flatMap((po) =>
      po.purchase_order_lines.map((item) =>
        assign({}, item, { po: omit(po, 'purchase_order_lines') }),
      ),
    )
    .filter('category')
    .map((item) => {
      const deliveryStatus = get(item, 'po.delivery_id') ? 'Yes' : 'No';

      assign(item, { deliveryStatus });

      return values(
        pickDeep(item, [
          'category',
          'po.po_number',
          'po.buyer_name',
          'po.due_date',
          'po.vendor.company_name',
          'item_name',
          'size',
          'quantity',
          'po.status',
          'top_status',
          'deliveryStatus',
          'po.delivery.booking.starts_at',
          'po.vendor_message',
        ]),
      ).map((v, i) => {
        if (!isEmpty(v) && includes([3, 11], i)) {
          return moment.utc(v).format('MM/DD/YYYY');
        }

        return v;
      });
    })
    .groupBy((v) => v[0])
    .mapValues((categoryGroup, category) => {
      if (!category || category === 'null') return categoryGroup;

      const totalsRow = range(13).map(() => '');
      totalsRow[0] = `${category}-Total`;
      totalsRow[7] = sumBy(categoryGroup, (v) => v[7]);
      return [...categoryGroup, totalsRow];
    })
    .values()
    .flatMap()
    .sortBy((v) => [v[0], v[4]])
    .value();

function makeLateOrdersSheet(openOrders, pendingOrders) {
  const lateOrdersHeaders = [
    'Category',
    'PO #',
    'Buyer Name',
    'Due Date',
    'Vendor',
    'Display Name',
    'Item Size',
    'Balance',
    'Status',
    'Top Status',
    'Delivery Scheduled',
    'Delivery Date',
    'Notes',
  ];

  const lateOrdersRows = [
    ...formatOrders(openOrders),
    ...formatOrders(pendingOrders),
  ];

  return XLSX.utils.aoa_to_sheet([lateOrdersHeaders, ...lateOrdersRows]);
}

function makeScheduledOrdersSheet(scheduledOrders) {
  const scheduledOrdersHeaders = [
    'PO #',
    'Due Date',
    'Buyer Name',
    'Delivery Date',
    'Delivery Time',
  ];

  const scheduledOrdersRows = chain(scheduledOrders)
    .sortBy((po) => get(po, 'delivery.booking.starts_at'))
    .map((po) => {
      const { buyer_name, po_number, due_date } = po;
      const deliveryDate = get(po, 'delivery.booking.starts_at');

      return [
        po_number,
        moment.utc(due_date).format('MM/DD/YYYY'),
        startCase(buyer_name),
        moment.utc(deliveryDate).format('MM/DD/YYYY'),
        moment.utc(deliveryDate).format('hh:mm a'),
      ];
    })
    .value();

  return XLSX.utils.aoa_to_sheet([
    scheduledOrdersHeaders,
    ...scheduledOrdersRows,
  ]);
}

export async function openOrdersExport() {
  const openOrders = await PurchaseOrderAPI.getOpenOrders().catch(noop);
  const pendingOrders = await PurchaseOrderAPI.getPendingOrders().catch(noop);
  const scheduledOrders =
    await PurchaseOrderAPI.getScheduledOrders().catch(noop);

  const lateOrdersWS = makeLateOrdersSheet(openOrders, pendingOrders);
  const scheduledOrdersWS = makeScheduledOrdersSheet(scheduledOrders);

  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(wb, lateOrdersWS, 'late orders');
  XLSX.utils.book_append_sheet(wb, scheduledOrdersWS, 'scheduled orders');
  XLSX.writeFile(wb, `late_orders_${new Date()}.xlsx`);
}

export async function warehouseDeliveriesExport(options) {
  const deliveries = await BookingAPI.getBookingsExport(options);
  let aggregatedDeliveries = null;
  aggregatedDeliveries = keyBy(deliveries, 'reference');
  const totalBoxes = [
    `Date: ${moment(options.date).format('L')} Total Boxes: ${sumBy(
      deliveries,
      'boxes',
    )}`,
  ];

  const headers = [
    'Time',
    'Reference',
    'Grade',
    'Vendor',
    'PO Number',
    'New or R/S',
    'Boxes',
    'Boxes Received',
    'Boxes Remaining',
    'Units',
    'Delivered At',
    'Notes',
  ];

  const rows = chain(deliveries)
    .sortBy(['starts_at', (d) => get(d, 'vendor.company_name')])
    .flatMap('purchase_orders')
    .compact()
    .map((po) => {
      let delivery = null;

      delivery = aggregatedDeliveries[po.delivery_reference];

      if (!delivery) return null;

      const {
        starts_at,
        reference,
        boxes,
        boxes_received,
        units,
        delivered_at,
        notes,
      } = delivery;
      const startsAt = moment.utc(starts_at).format('h:mm A');
      const vendorGrade = get(delivery, 'vendor.grade');
      const vendorName = toUpper(get(delivery, 'vendor.company_name'));
      const poNumber = toUpper(po.po_number);
      const newOrRS = po.new_item ? 'NEW' : 'R/S';
      const remaining = boxes - (boxes_received ? +boxes_received : 0);

      return [
        startsAt,
        reference,
        vendorGrade,
        vendorName,
        poNumber,
        newOrRS,
        boxes,
        boxes_received,
        remaining,
        units,
        delivered_at,
        notes,
      ];
    })
    .compact()
    .value();

  const ws = XLSX.utils.aoa_to_sheet([totalBoxes, headers, ...rows]);

  let rowCount = 1;

  const totalBoxesMerge = { s: { c: 0, r: 0 }, e: { c: 4, r: 0 } };
  const merges = chain(deliveries)
    .sortBy(['starts_at', (d) => get(d, 'vendor.company_name')])
    .flatMap((d) => {
      const firstRow = rowCount + 1;
      rowCount += size(d.purchase_orders);

      return [
        { s: { c: 0, r: firstRow }, e: { c: 0, r: rowCount } },
        { s: { c: 1, r: firstRow }, e: { c: 1, r: rowCount } },
        { s: { c: 2, r: firstRow }, e: { c: 2, r: rowCount } },
        { s: { c: 3, r: firstRow }, e: { c: 3, r: rowCount } },
        { s: { c: 6, r: firstRow }, e: { c: 6, r: rowCount } },
        { s: { c: 7, r: firstRow }, e: { c: 7, r: rowCount } },
        { s: { c: 8, r: firstRow }, e: { c: 8, r: rowCount } },
        { s: { c: 9, r: firstRow }, e: { c: 9, r: rowCount } },
        { s: { c: 10, r: firstRow }, e: { c: 10, r: rowCount } },
        { s: { c: 11, r: firstRow }, e: { c: 11, r: rowCount } },
      ];
    })
    .value();

  ws['!merges'] = [totalBoxesMerge, ...merges];
  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(wb, ws, '');
  XLSX.writeFile(wb, `schedule_${new Date()}.xlsx`);

  return deliveries;
}

export async function billPaymentsExport(userId) {
  const payments = await UserAPI.getBillPaymentsExport(userId);
  downloadAsCSV(payments, paymentColumns, 'bill_payments');
  return payments;
}

export async function purchaseOrdersExport(userId) {
  const orders = await UserAPI.getPurchaseOrdersExport(userId);
  const formatted = map(orders, (o) => ({
    ...o,
    topStatus: makeTopStatuses(o, false),
  }));
  downloadAsCSV(formatted, orderColumns, 'purchase_orders');
  return orders;
}

export async function vendorBillsExport(userId) {
  const bills = await UserAPI.getVendorBillsExport(userId);
  downloadAsCSV(bills, billColumns, 'vendor_bills');
  return bills;
}

export async function vendorGradeExport() {
  const vendors = await VendorAPI.getVendorsExport();
  downloadAsCSV(vendors, vendorGradeColumns, 'vendors');
  return vendors;
}

export async function billCreditsExport(userId) {
  const credits = await UserAPI.getBillCreditsExport(userId);
  downloadAsCSV(credits, creditColumns, 'bill_credits');
  return credits;
}

export async function factoriesExport(params) {
  const factories = await FactoryAPI.getAll(params).catch(console.error);
  const formatted = map(factories.rows, (f) => ({
    ...f,
    status: makeFactoryStatus(f),
  }));

  downloadAsCSV(formatted, factoryColumns, 'factories');
  return factories;
}

export async function complianceVendorsReport() {
  const vendors = await VendorAPI.generateComplianceReport();

  if (!vendors || vendors.length === 0) {
    throw new Error('No data found for the selected criteria');
  }

  const transformedVendors = vendors.map((vendor) => {
    const vendorUser = vendor.associated_users?.find(
      (user) => user.role === 'VENDOR',
    );

    // Display date only without time zone
    const coiDate = vendor.coi_expiration_date
      ? DateTime.fromISO(vendor.coi_expiration_date, { zone: 'utc' }).toFormat(
          'yyyy-MM-dd',
        )
      : '';

    return {
      company_name: vendor.company_name,
      email: vendorUser?.email || '',
      last_logged_in_at: vendorUser?.last_logged_in_at || '',
      coi_expiration_date: coiDate,
    };
  });

  downloadAsCSV(transformedVendors, complianceReportColumns, 'vendors');
  return vendors;
}

export async function influencersExport() {
  const influencers = await InfluencerAPI.getInfluencersExport();

  downloadAsCSV(influencers, influencersColumns, 'influencers');
  return influencers;
}

export async function catalogItemsExport(userId, params) {
  const catalogItems = await CatalogItemAPI.getCatalogItemsExport(
    userId,
    params,
  );
  downloadAsCSV(catalogItems, catalogItemColumns, 'catalog_items');
  return catalogItems;
}
